import { put, call, takeLatest, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { types, updateReloadCountDown } from '../../actions/backendCommon';
import { isReadyToReload } from '../../selectors/call';
import { captureException, captureWarnMessage } from '../../../tracker/raven';
import { reloaderLogger } from '../../../utils/logger/loggerFactory';
import { reloadPage } from '../../../utils/browser';

const reloadLog = reloaderLogger;

interface ReloadPayload {
    isImmediate?: boolean;
    code?: string | number | null;
    reason?: string | null;
    countdown?: number;
    log?: boolean;
    isCallStarted?: boolean;
}

interface ReloadAction {
    type?: string;
    payload?: ReloadPayload;
}

function reload() {
    if (localStorage.getItem('_pauseOnReload')) {
        // eslint-disable-next-line no-debugger
        debugger;
    }

    if (process.env.NODE_ENV === 'development' && localStorage.getItem('_disableReload')) {
        reloadLog.warn('Reloading disabled. Only for dev mode');
        return;
    }

    reloadPage();
}

function toReloadReason(reason: ReloadPayload['reason']): string {
    return typeof reason === 'string' ? reason : String(reason ?? '');
}

function* waitAllDaemonConnectionsClosedAndReload(
    reason: ReloadPayload['reason'],
    isCallStarted: boolean
): Generator<unknown, void, unknown> {
    while (true) {
        const readyToReload = Boolean(yield select(isReadyToReload));
        if (readyToReload) {
            break;
        }
        yield call(delay, 200);
    }

    const logMessage = `App will be reloaded. Reason: ${toReloadReason(reason)}`;
    reloadLog.warn(logMessage);
    if (isCallStarted) {
        captureException(new Error(`Application reload happens during the call. ${logMessage}`));
    }
    reload();
}

export function* doReload(action: ReloadAction): Generator<unknown, void, unknown> {
    try {
        const payload = action.payload || {};
        if (payload.isImmediate) {
            reloadLog.log('RELOAD IMMEDIATE');
            const { code, reason } = payload;

            if (payload.log) {
                captureWarnMessage(`RELOAD IMMEDIATE. Code: ${String(code ?? '')}. Reason: ${toReloadReason(reason)}`);
            }
        } else {
            const { code, countdown, reason } = payload;
            reloadLog.log(`RELOAD after ${countdown}sec. Code: ${String(code ?? '')}. Reason: ${toReloadReason(reason)}`);

            if (payload.log) {
                captureWarnMessage(`RELOAD after ${countdown}sec. Code: ${String(code ?? '')}. Reason: ${toReloadReason(reason)}`);
            }
        }

        yield call(waitAllDaemonConnectionsClosedAndReload, payload.reason, payload.isCallStarted === true);
    } catch (error) {
        captureException(error);
        if (error instanceof Error) {
            reloadLog.error(error.message, error);
        } else {
            reloadLog.error('Reload saga failed', error);
        }
    }
}

export function* doCountDown() {
    while (true) {
        yield call(delay, 1000);
        yield put(updateReloadCountDown());
    }
}

export function* initReloadCountDown() {
    yield takeLatest(types.RELOAD, doCountDown);
}

export function* handleReload() {
    yield takeLatest(types.RELOAD, doReload);
}

export default [initReloadCountDown(), handleReload()];
